import { CommonModule } from '@angular/common';
import { Component, computed, inject, Inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import {
    MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { JsonPathViewerComponent } from '../../../common/json-path-viewer/json-path-viewer.component';
import { UserData, UserDataUpsertRequest } from '../../../api/user-data-service';
import { PageHeaderComponent } from '@nucleus/ui-base';
import { SchemaFormComponent } from '../../../common/schema-form/schema-form.component';
import { UserDataTypeService } from '../../../services/userdata-type/userdata-type.service';
import { UserDataTypeAttribute } from '../../../services/userdata-type/userdata-type.models';

/** One org-scope autocomplete suggestion sourced from the org_unit catalog.
 *  Stored as `code` on user_data; rendered as "code — name" in the dropdown. */
export interface OrgScopeOption {
    code: string;
    name: string;
}

/**
 * Dialog input shape. {@code mode='create'} renders an empty form with
 * type-autocomplete; {@code mode='edit'} pre-fills + disables (type, key)
 * since changing the natural key would create a new row instead of editing
 * the existing one (the backend upsert is by (type, key) — no rename
 * support today).
 */
export interface UserDataEditDialogData {
    mode: 'create' | 'edit';
    userData?: UserData;
    knownTypes: string[];
    knownDepartments?: OrgScopeOption[];
    knownOrgUnits?: OrgScopeOption[];
    knownFinancialUnits?: OrgScopeOption[];
}

/**
 * Closing the dialog with a non-undefined value triggers the parent's save
 * handler. {@code undefined} = user cancelled, no-op.
 */
export type UserDataEditDialogResult = UserDataUpsertRequest | undefined;

@Component({
    selector: 'app-user-data-edit',
    standalone: true,
    imports: [
        CommonModule, ReactiveFormsModule,
        MatFormFieldModule, MatInputModule, MatAutocompleteModule,
        MatSlideToggleModule, MatButtonModule, MatIconModule,
        MatTooltipModule, MatExpansionModule,
        MatDialogActions, MatDialogClose,
        JsonPathViewerComponent,
        PageHeaderComponent,
        SchemaFormComponent,
    ],
    templateUrl: './user-data-edit.component.html',
    styleUrl: './user-data-edit.component.css',
})
export class UserDataEditComponent {

    private fb = inject(FormBuilder);
    private userDataTypeService = inject(UserDataTypeService);
    public dialogRef = inject<MatDialogRef<UserDataEditComponent, UserDataEditDialogResult>>(
            MatDialogRef);

    /** Declared attributes for the currently-selected type (or empty if the type
     *  has none / isn't observed yet). Drives whether the schema-form section
     *  renders at all + what fields it exposes. Re-fetched on every type change. */
    readonly attributes = signal<UserDataTypeAttribute[]>([]);
    /** Synthesized JSON Schema derived from {@link attributes}. Null = no schema
     *  available, fall back to raw-JSON editing. */
    readonly schemaForType = computed<any | null>(() => {
        const attrs = this.attributes();
        if (!attrs.length) return null;
        return this.buildSchema(attrs);
    });

    /** Type-options for the currently-selected type (resource defaults + cost
     *  defaults + capacity unit). Used as placeholders / hints in the Resource
     *  card so the operator sees the type-level default they're overriding. */
    readonly typeOptions = signal<{
        resourceCapacityUnit?: string | null;
        reusableDefault?: boolean;
        availabilityDefault?: string | null;
        costPerUseDefault?: number | null;
        costCurrencyDefault?: string | null;
    } | null>(null);
    /** Snapshot of the parsed payload projected through the schema's declared
     *  paths — passed as `[value]` to <app-schema-form>. Recomputed whenever
     *  the raw `data` control or the schema changes. */
    readonly schemaValue = computed<any>(() => {
        const schema = this.schemaForType();
        if (!schema) return null;
        return this.parsedJson() ?? {};
    });

    readonly mode: 'create' | 'edit';
    readonly knownTypes:          string[];
    readonly knownDepartments:    OrgScopeOption[];
    readonly knownOrgUnits:       OrgScopeOption[];
    readonly knownFinancialUnits: OrgScopeOption[];
    readonly form: FormGroup;
    readonly jsonError = signal<string | null>(null);
    /** When true, the JSON region renders an editable textarea; otherwise it
     *  shows the read-only ngx-json-viewer (same widget the audit-trail
     *  detail dialog uses). Toggled via the pencil/check button on the
     *  payload header. */
    readonly editingJson = signal<boolean>(false);
    /** Pretty-vs-compact toggle for the textarea. */
    readonly pretty = signal<boolean>(true);

    /** Live-filtered autocomplete sources. */
    filteredTypes:          string[];
    filteredDepartments:    OrgScopeOption[];
    filteredOrgUnits:       OrgScopeOption[];
    filteredFinancialUnits: OrgScopeOption[];

    /** Live-tracked text from the `data` form control. Feeds parsedJson(). */
    private readonly dataText;
    /** Parsed JSON tree for the read-only viewer. Returns null when the
     *  textarea content can't be parsed — template falls back to a "no
     *  payload" placeholder. */
    readonly parsedJson;

    constructor(@Inject(MAT_DIALOG_DATA) public data: UserDataEditDialogData) {
        this.mode = data.mode;
        this.knownTypes          = data.knownTypes          ?? [];
        this.knownDepartments    = data.knownDepartments    ?? [];
        this.knownOrgUnits       = data.knownOrgUnits       ?? [];
        this.knownFinancialUnits = data.knownFinancialUnits ?? [];
        this.filteredTypes          = [...this.knownTypes];
        this.filteredDepartments    = [...this.knownDepartments];
        this.filteredOrgUnits       = [...this.knownOrgUnits];
        this.filteredFinancialUnits = [...this.knownFinancialUnits];

        const seed = data.userData;
        const seedAny = seed as any;        // department/orgUnit/financialUnit live on the
                                            // generated UserData type post-regen; cast keeps
                                            // this resilient if the regen output drifts.
        this.form = this.fb.group({
            type:     [{ value: seed?.type  ?? '', disabled: this.mode === 'edit' },
                       [Validators.required, Validators.maxLength(64)]],
            key:      [{ value: seed?.key   ?? '', disabled: this.mode === 'edit' },
                       [Validators.required, Validators.maxLength(255)]],
            label:    [seed?.label ?? '',  [Validators.maxLength(1024)]],
            data:     [this.prettyJsonSeed(seed?.data) ?? '{}', []],
            // v2 — canonical isResource flag. The deprecated v1 `resource`
            // boolean is still mirrored server-side, so seed it from either
            // shape and emit on isResource only (UserDataUpsertRequest).
            isResource:        [seedAny?.isResource ?? seed?.resource ?? false],
            capacity:          [seedAny?.capacity ?? null,
                                [Validators.min(0)]],
            reusable:          [seedAny?.reusable ?? null],
            capacityRemaining: [seedAny?.capacityRemaining ?? null,
                                [Validators.min(0)]],
            availability:      [this.prettyJsonSeed(seedAny?.availability) ?? '',
                                []],
            costPerUse:        [seedAny?.costPerUse ?? null,
                                [Validators.min(0)]],
            costCurrency:      [seedAny?.costCurrency ?? '',
                                [Validators.maxLength(3)]],
            department:    [seedAny?.department    ?? '', [Validators.maxLength(64)]],
            orgUnit:       [seedAny?.orgUnit       ?? '', [Validators.maxLength(64)]],
            financialUnit: [seedAny?.financialUnit ?? '', [Validators.maxLength(64)]],
        });

        // Live JSON validation + autocomplete filtering.
        this.form.controls['data'].valueChanges.subscribe((v: string) => {
            this.jsonError.set(this.validateJson(v));
        });
        this.jsonError.set(this.validateJson(this.form.controls['data'].value));

        this.form.controls['type'].valueChanges.subscribe((v: string) => {
            this.filteredTypes = this.filterMatching(this.knownTypes, v);
            this.fetchAttributesFor(v);
            this.fetchTypeOptionsFor(v);
        });
        // First-load fetch for the seeded type (edit mode) — valueChanges
        // doesn't fire for the initial setValue so we kick it off manually.
        if (seed?.type) {
            this.fetchAttributesFor(seed.type);
            this.fetchTypeOptionsFor(seed.type);
        }
        this.form.controls['department'].valueChanges.subscribe((v: string) => {
            this.filteredDepartments = this.filterOrgScope(this.knownDepartments, v);
        });
        this.form.controls['orgUnit'].valueChanges.subscribe((v: string) => {
            this.filteredOrgUnits = this.filterOrgScope(this.knownOrgUnits, v);
        });
        this.form.controls['financialUnit'].valueChanges.subscribe((v: string) => {
            this.filteredFinancialUnits = this.filterOrgScope(this.knownFinancialUnits, v);
        });

        // Bridge `data` control into a signal so the read-only viewer
        // re-renders when the textarea (or any setValue call) updates the
        // form. valueChanges fires AFTER seed, so prime with initial value.
        this.dataText = toSignal(
            this.form.controls['data'].valueChanges,
            { initialValue: this.form.controls['data'].value as string });
        this.parsedJson = computed<unknown>(() => {
            const raw = this.dataText() ?? '';
            if (!raw.trim()) return null;
            try { return JSON.parse(raw); }
            catch { return null; }
        });
    }

    private filterMatching(source: string[], needle: string): string[] {
        const n = (needle ?? '').toLowerCase();
        return source.filter(s => s.toLowerCase().includes(n));
    }

    private filterOrgScope(source: OrgScopeOption[], needle: string): OrgScopeOption[] {
        const n = (needle ?? '').toLowerCase();
        if (!n) return [...source];
        return source.filter(o =>
            o.code.toLowerCase().includes(n) ||
            o.name.toLowerCase().includes(n));
    }

    private validateJson(value: string): string | null {
        if (!value || !value.trim()) return null;
        try {
            JSON.parse(value);
            return null;
        } catch (e: any) {
            return e?.message ?? 'Invalid JSON';
        }
    }

    canSave(): boolean {
        return this.form.valid && this.jsonError() === null;
    }

    onSave(): void {
        if (!this.canSave()) return;
        const v = this.form.getRawValue();
        const isResource = !!v.isResource;
        // Availability is round-tripped as a JSON string. Allow null on a blank
        // field so the backend treats it as "use type default / always avail".
        const availabilityRaw = (v.availability ?? '').trim();
        const out: UserDataUpsertRequest = {
            type:              v.type.trim(),
            key:               v.key.trim(),
            label:             v.label?.trim() || undefined,
            data:              v.data?.trim() || undefined,
            isResource,
            // Resource-card fields are only meaningful when isResource is on —
            // suppress sending them otherwise to avoid spurious defaults
            // overwriting a row that's just being demoted to non-resource.
            capacity:          isResource ? (this.numOrUndef(v.capacity))           : undefined,
            reusable:          isResource ? (v.reusable === null ? undefined : !!v.reusable) : undefined,
            capacityRemaining: isResource ? (this.numOrUndef(v.capacityRemaining))  : undefined,
            availability:      isResource ? (availabilityRaw || undefined)         : undefined,
            costPerUse:        isResource ? (this.numOrUndef(v.costPerUse))        : undefined,
            costCurrency:      isResource
                                  ? (v.costCurrency?.trim().toUpperCase() || undefined)
                                  : undefined,
            department:        v.department?.trim()    || undefined,
            orgUnit:           v.orgUnit?.trim()       || undefined,
            financialUnit:     v.financialUnit?.trim() || undefined,
        };
        this.dialogRef.close(out);
    }

    private numOrUndef(value: unknown): number | undefined {
        if (value === null || value === undefined || value === '') return undefined;
        const n = Number(value);
        return Number.isFinite(n) ? n : undefined;
    }

    private prettyJsonSeed(raw: string | undefined | null): string | undefined {
        if (!raw || !raw.trim()) return raw ?? undefined;
        try {
            return JSON.stringify(JSON.parse(raw), null, 2);
        } catch {
            return raw;
        }
    }

    /** Toggle the JSON region between read-only viewer and editable textarea.
     *  Switching INTO edit mode is unconditional. Switching OUT (to viewer)
     *  is only allowed when the current text parses, so the operator can't
     *  trap themselves in a state where the viewer can't render. */
    toggleEditMode(): void {
        if (this.editingJson()) {
            // returning to viewer — only if parseable
            if (this.jsonError() !== null) return;
            this.editingJson.set(false);
        } else {
            this.editingJson.set(true);
        }
    }

    togglePretty(): void {
        const ctl = this.form.controls['data'];
        const raw = ctl.value;
        if (!raw || !raw.trim()) return;
        let parsed: any;
        try { parsed = JSON.parse(raw); } catch { return; }
        const next = !this.pretty();
        ctl.setValue(next
                ? JSON.stringify(parsed, null, 2)
                : JSON.stringify(parsed));
        this.pretty.set(next);
    }

    formatJson(): void {
        const ctl = this.form.controls['data'];
        const raw = ctl.value;
        if (!raw || !raw.trim()) return;
        try {
            ctl.setValue(JSON.stringify(JSON.parse(raw), null, 2));
            this.pretty.set(true);
        } catch {
            // Don't reformat invalid JSON — would lose the user's draft.
        }
    }

    // ── Schema-driven editor (P3.1) ─────────────────────────────────────────

    /** Fetch declared attributes for `type` so the schema-form can render. A
     *  blank or unknown type clears the attributes (which collapses the form
     *  back to raw-JSON-only). Errors are swallowed silently — the dialog
     *  still works, just without the schema-driven layer. */
    private fetchAttributesFor(type: string | null | undefined): void {
        const t = (type ?? '').trim();
        if (!t) {
            this.attributes.set([]);
            return;
        }
        this.userDataTypeService.listAttributes({
            onStart:    () => {},
            onSuccess:  (rows) => this.attributes.set(rows ?? []),
            onError:    ()    => this.attributes.set([]),
            onComplete: () => {},
        }, t);
    }

    /** Fetch type-options for {@code type} so the Resource card can surface
     *  type-level defaults as placeholders (capacity unit, reusable default,
     *  cost-per-use default, cost-currency default). A blank/unknown type
     *  clears it. Errors are swallowed — the card still works, just without
     *  the inline default hints. */
    private fetchTypeOptionsFor(type: string | null | undefined): void {
        const t = (type ?? '').trim();
        if (!t) {
            this.typeOptions.set(null);
            return;
        }
        this.userDataTypeService.getOptions({
            onStart:    () => {},
            onSuccess:  (o) => this.typeOptions.set(o ?? null),
            onError:    () => this.typeOptions.set(null),
            onComplete: () => {},
        }, t);
    }

    /** Convert the type's attribute rules into a JSON-schema object. Each
     *  attribute's `payloadPath` (e.g. `$.firmware`, `$.metadata.vendor`)
     *  becomes a property path; `valueType` maps to the JSON-schema type;
     *  `description` carries through. Nested object paths are synthesized
     *  on the fly. Non-indexed attributes still get rendered — they're just
     *  not filterable in the list view. */
    private buildSchema(attrs: UserDataTypeAttribute[]): any {
        const root: any = { type: 'object', properties: {}, required: [] };

        for (const a of attrs) {
            const raw = (a.payloadPath ?? '').trim();
            if (!raw) continue;
            const path = raw.startsWith('$.') ? raw.slice(2) : raw;
            const parts = path.split('.').filter(p => p.length > 0);
            if (!parts.length) continue;

            // Walk into the property tree, creating object containers as needed.
            let cursor = root.properties;
            for (let i = 0; i < parts.length - 1; i++) {
                const seg = parts[i];
                if (!cursor[seg] || cursor[seg].type !== 'object') {
                    cursor[seg] = { type: 'object', title: seg, properties: {} };
                }
                cursor = cursor[seg].properties;
            }
            const leaf = parts[parts.length - 1];

            cursor[leaf] = {
                type: a.valueType === 'integer' ? 'integer' : 'string',
                title: a.name || leaf,
                description: a.description,
            };
            // semver is a string with a permissive pattern hint.
            if (a.valueType === 'semver') {
                cursor[leaf].pattern = '^[0-9]+(\\.[0-9]+){0,2}([-+].+)?$';
            }
        }
        return root;
    }

    /** Schema-form fired (valueChange) — merge the form value into the raw
     *  JSON payload (declared paths overwrite, non-declared paths preserved)
     *  and push the result through the `data` form control so the existing
     *  parsed-json signal + viewer all stay in sync. */
    onSchemaFormChange(value: any): void {
        if (value === null || value === undefined) return;
        const ctl = this.form.controls['data'];
        let current: any = {};
        try {
            current = ctl.value ? JSON.parse(ctl.value) : {};
            if (current === null || typeof current !== 'object' || Array.isArray(current)) {
                current = {};
            }
        } catch {
            current = {};
        }
        const merged = this.deepMerge(current, value);
        const next = this.pretty()
            ? JSON.stringify(merged, null, 2)
            : JSON.stringify(merged);
        // Suppress the dataText signal feedback loop: setValue with the
        // emitEvent flag controls whether valueChanges fires. We DO want it
        // to fire so jsonError + parsedJson recompute, just not loop the
        // schema-form back into itself (the schema-form binds [value] from
        // the computed signal — which only emits when the parsed result
        // structurally changes, so the loop is naturally damped).
        ctl.setValue(next);
    }

    /** Deep-merge `patch` into `base`. Plain objects merge recursively;
     *  arrays + primitives replace wholesale. Doesn't mutate `base`. */
    private deepMerge(base: any, patch: any): any {
        if (patch === null || typeof patch !== 'object' || Array.isArray(patch)) {
            return patch;
        }
        const out: any = { ...(base ?? {}) };
        for (const k of Object.keys(patch)) {
            const pv = patch[k];
            if (pv === null || pv === undefined) continue;     // skip empties
            if (typeof pv === 'object' && !Array.isArray(pv)) {
                out[k] = this.deepMerge(out[k], pv);
            } else {
                out[k] = pv;
            }
        }
        return out;
    }
}
